import { HierarchyType } from '../../hierarchy';
import { DatePeriodParameterOptions, DatePickerParameterOptions, HierarchyTreeParameterOptions, RadioButtonParameterOptions, SelectParameterOptions } from './parameters';
import { ReportParameterDefinition } from './report-parameter.model';

export type ReportParameterKeys = keyof typeof REPORT_PARAMETER_REGISTRY;

export const REPORT_PARAMETER_REGISTRY = {
    subscriberKey: {
        key: 'subscriberKey',
        type: 'subscriberSelect',
        label: 'Subscriber',
        required: true,
        defaultValue: null,
        emptyValue: null,
        valueType: 'number',
        securityRule: 'sysAdmin'
    },
    datePeriod: {
        key: 'datePeriod',
        type: 'datePeriod',
        label: 'Date Period',
        placeholder: 'Select a date period',
        renderLabel: false,
        required: true,
        defaultValue: null,
        emptyValue: null,
        valueType: 'string',
        additionalParameters: [
            { key: 'startDate', valueType: 'date', defaultValue: null, emptyValue: null },
            { key: 'endDate', valueType: 'date', defaultValue: null, emptyValue: null }
        ],
        options: {
            startDateParamKey: 'startDate',
            startDateLabel: 'Start Date',
            endDateParamKey: 'endDate',
            endDateLabel: 'End Date',
            dateRangeMaxYears: 3
        } as DatePeriodParameterOptions
    },
    startDate: {
        key: 'startDate',
        type: 'datePicker',
        label: 'Start Date',
        placeholder: 'Select start date',
        required: true,
        defaultValue: null,
        emptyValue: null,
        valueType: 'date',
        options: {
            //maxDate: fromDateOnlyString('2026-02-02'),
            maxDateParameterKey: 'endDate'
        } as DatePickerParameterOptions
    },
    endDate: {
        key: 'endDate',
        type: 'datePicker',
        label: 'End Date',
        placeholder: 'Select end date',
        required: true,
        defaultValue: null,
        emptyValue: null,
        valueType: 'date',
        options: {
            //minDate: new Date('2026-01-15'),
            minDateParameterKey: 'startDate'
        } as DatePickerParameterOptions
    },
    divisionHierarchyNodeKeys: {
        key: 'divisionHierarchyNodeKeys',
        type: 'hierarchyTreeSelect',
        label: 'Division Hierarchy Nodes',
        placeholder: 'Select division nodes',
        required: false,
        defaultValue: [],
        emptyValue: [],
        valueType: 'number[]',
        options: {
            hierarchyType: HierarchyType.Division
        } as HierarchyTreeParameterOptions
    },
    deliveryCenterHierarchyNodeKeys: {
        key: 'deliveryCenterHierarchyNodeKeys',
        type: 'hierarchyTreeSelect',
        label: 'Delivery Center Hierarchy Nodes',
        placeholder: 'Select delivery center nodes',
        required: false,
        defaultValue: [],
        emptyValue: [],
        valueType: 'number[]',
        options: {
            hierarchyType: HierarchyType.DeliveryCenter
        } as HierarchyTreeParameterOptions
    },
    serviceCenterHierarchyNodeKeys: {
        key: 'serviceCenterHierarchyNodeKeys',
        type: 'hierarchyTreeSelect',
        label: 'Service Center Hierarchy Nodes',
        placeholder: 'Select service center nodes',
        required: false,
        defaultValue: [],
        emptyValue: [],
        valueType: 'number[]',
        options: {
            hierarchyType: HierarchyType.ServiceCenter
        } as HierarchyTreeParameterOptions
    },
    productCategoryCostKey: {
        key: 'productCategoryCostKey',
        type: 'productCategoryCostSelect',
        label: 'Product Cost Category',
        placeholder: 'Select a product cost category',
        required: false,
        defaultValue: null,
        emptyValue: null,
        valueType: 'number',
    },
    productCategoryReportKeys: {
        key: 'productCategoryReportKeys',
        type: 'productCategoryReportMultiSelect',
        label: 'Products',
        placeholder: 'Select products',
        required: false,
        defaultValue: [],
        emptyValue: [],
        valueType: 'number[]',
    },
    tradeClassKeys: {
        key: 'tradeClassKeys',
        type: 'tradeClassMultiSelect',
        label: 'Trade Classes',
        placeholder: 'Select trade classes',
        required: false,
        defaultValue: [],
        emptyValue: [],
        valueType: 'number[]',
    },
    deliveryTypeKeys: {
        key: 'deliveryTypeKeys',
        type: 'deliveryTypeMultiSelect',
        label: 'BRITE Delivery Types',
        placeholder: 'Select delivery types',
        required: false,
        defaultValue: [],
        emptyValue: [],
        valueType: 'number[]'
    },
    transactionDateType: {
        key: 'transactionDateType',
        type: 'radioButton',
        label: 'Date Period Applies To',
        required: false,
        defaultValue: 'TransactionDate',
        emptyValue: 'TransactionDate',
        valueType: 'string',
        options: {
            options: [
                { key: 'TransactionDate', label: 'Transaction Date' },
                { key: 'PostedDate', label: 'Posting Date' }
            ]
        } as RadioButtonParameterOptions
    },
    customerAverageAnnualDeliveredUnits: {
        key: 'customerAverageAnnualDeliveredUnits',
        type: 'customerAverageAnnualDeliveredUnits',
        label: 'Customer Average Annual Delivered Units',
        placeholder: 'Input average annual delivered units',
        required: true,
        defaultValue: 0,
        emptyValue: 0,
        valueType: 'number'
    },
    includeLongCapPremiums: {
        key: 'includeLongCapPremiums',
        type: 'checkbox',
        label: 'Include Long Cap Premiums',
        required: false,
        defaultValue: false,
        emptyValue: false,
        valueType: 'boolean'
    },
    hedgeReportDateRange: {
         key: 'hedgeReportDateRange',
        type: 'select',
        label: 'Date Range',
        placeholder: 'Select a date range',
        required: true,
        defaultValue: 12,
        emptyValue: 12,
        valueType: 'number',
        options: {
            options: [
                { value: 12, label: '12 Months' },
                { value: 18, label: '18 Months' }
            ]
        } as SelectParameterOptions
    },
    inventoryTransactionTypeKeys: {
        key: 'inventoryTransactionTypeKeys',
        type: 'inventoryTransactionTypeMultiSelect',
        label: 'Transaction Type',
        placeholder: 'Select transaction types',
        required: false,
        defaultValue: [],
        emptyValue: [],
        valueType: 'number[]',
    }
} satisfies Record<string, ReportParameterDefinition<any>>;
